import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  ArrayValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminCosmeticAttributes,
  AdminCosmeticCharacteristics,
  AdminCosmeticDetailsForm,
  AdminCosmeticDetailsFormGroup,
  AdminCosmeticStatus,
} from '@web/shared/module/cosmetic';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
  MultimediaChooseOrUploadMultipleControlComponent,
} from '@web/shared/module/multimedia';
import { AdminCosmeticCharacteristicsControlComponent } from '../admin-cosmetic-characteristics-control/admin-cosmetic-characteristics-control.component';
import { AdminBrandSelectItemsProviderService } from '@web/shared/module/brand';
import { Observable, of } from 'rxjs';
import { Select, SelectFilterEvent } from 'primeng/select';
import { AdminCosmeticAttributesControlComponent } from '../admin-cosmetic-attributes-control/admin-cosmetic-attributes-control.component';
import { InputNumber } from 'primeng/inputnumber';
import { AdminCosmeticStatusProviderService } from '../../providers/admin-cosmetic-status-provider.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  WarehouseGoodType,
  WarehouseGoodUsageStatusComponent,
  AdminWarehouseGoodDataSourceService,
  AdminWarehouseGoodSelectItemsProviderService,
  adminWarehouseGoodSelectItemsWithCurrentSelection,
} from '@web/features/warehouse';

@Component({
  selector: 'app-admin-cosmetic-details-form',
  imports: [
    AdminCosmeticAttributesControlComponent,
    AdminCosmeticCharacteristicsControlComponent,
    Button,
    CommonModule,
    FormControlComponent,
    FormsModule,
    InputNumber,
    InputText,
    MultimediaChooseOrUploadControlComponent,
    MultimediaChooseOrUploadMultipleControlComponent,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    WarehouseGoodUsageStatusComponent,
  ],
  templateUrl: './admin-cosmetic-details-form.component.html',
  styleUrl: './admin-cosmetic-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminCosmeticDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly brandSelectItemsProvider = inject(AdminBrandSelectItemsProviderService);
  /* prettier-ignore */
  private readonly cosmeticStatusProvider = inject(AdminCosmeticStatusProviderService);
  /* prettier-ignore */
  private readonly warehouseGoodDataSource = inject(AdminWarehouseGoodDataSourceService);
  /* prettier-ignore */
  private readonly warehouseGoodSelectItemsProvider = inject(AdminWarehouseGoodSelectItemsProviderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /* prettier-ignore */
  public readonly canBeWarehouseGoodEdited = input<Nillable<boolean>>(true);
  public readonly data = input<Nillable<AdminCosmeticDetailsForm>>();
  public readonly isEditContext = input<boolean>(false);
  public readonly submitClickEvent = output<AdminCosmeticDetailsForm>();

  /* prettier-ignore */
  protected readonly cosmeticStatusSelectItems$ = this.cosmeticStatusSelectItems();
  protected readonly brandSelectItems$ = this.brandSelectItems();
  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly warehouseGoodType = WarehouseGoodType;
  /* prettier-ignore */
  protected warehouseGoodSelectItems$: Observable<SelectItem<Guid>[]> = of([]);
  protected currentGoodId$!: Observable<Nillable<Guid>>;

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    effect(() => this.configureWarehouseGoodAvailability());
  }

  public ngOnInit(): void {
    this.listenToStatusValueChanges();
    this.configureWarehouseGoodSelectItemsSource();
    this.configureCurrentGoodIdSource();

    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleWarehouseGoodSelectFilterChange(event: SelectFilterEvent): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems(
      event.filter.toLowerCase(),
    );

    this.changeDetectorRef.detectChanges();
  }

  private listenToStatusValueChanges(): void {
    this.form.controls.status.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        const isPublic = this.isPrivateOrPublic(status);

        this.configureBrandRequirements(isPublic);
        this.configureFeaturedFileRequirements(isPublic);
        this.configureFilesRequirements(isPublic);
        this.configurePriceRequirements(isPublic);
      });
  }

  private isPrivateOrPublic(status: Nillable<AdminCosmeticStatus>): boolean {
    if (isNil(status)) {
      return false;
    }

    /* prettier-ignore */
    return [
      AdminCosmeticStatus.Public,
      AdminCosmeticStatus.Private,
    ].includes(status);
  }

  private toData(): AdminCosmeticDetailsForm {
    const data = this.form.getRawValue();

    return new AdminCosmeticDetailsForm({
      attributes: data.attributes,
      characteristics: data.characteristics,
      cosmeticBrandId: data.cosmeticBrandId,
      featuredFile: data.featuredFile,
      files: data.files,
      name: data.name,
      price: data.price,
      slug: data.slug,
      status: valueOrDefault(data.status, AdminCosmeticStatus.Draft),
      vendorUrl: data.vendorUrl,
      warehouseProductId: data.warehouseProductId,
    });
  }

  private buildForm(): AdminCosmeticDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      attributes: this.builder.control<Nillable<AdminCosmeticAttributes>>(null),
      characteristics: this.builder.control<Nillable<AdminCosmeticCharacteristics>>(null),
      cosmeticBrandId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      featuredFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null, [Validators.required]),
      files: this.builder.control<Nillable<AdminMultimediaImageUpload[]>>(null, [Validators.required, ArrayValidator.min(AdminCosmeticDetailsForm.FilesMinCount)]),
      name: this.builder.nonNullable.control<string>('', [Validators.required]),
      price: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(AdminCosmeticDetailsForm.PriceMin)]),
      slug: this.builder.control<Nillable<string>>(null),
      status: this.builder.control<Nillable<AdminCosmeticStatus>>(null, [Validators.required]),
      vendorUrl: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminCosmeticDetailsForm.VendorUrlMaxLength)]),
      warehouseProductId: this.builder.control<Nillable<Guid>>(null),
    });
  }

  private brandSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.brandSelectItemsProvider.fetchCollectionAsSelectItems('id');
  }

  private configureWarehouseGoodSelectItemsSource(): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems();
    this.changeDetectorRef.detectChanges();
  }

  private configureCurrentGoodIdSource(): void {
    this.currentGoodId$ = this.form.controls.warehouseProductId.valueChanges;
    this.changeDetectorRef.detectChanges();
  }

  private warehouseGoodSelectItems(
    query: Nillable<string> = null,
  ): Observable<SelectItem<Guid>[]> {
    return adminWarehouseGoodSelectItemsWithCurrentSelection({
      control: this.form.controls.warehouseProductId,
      dataSource: this.warehouseGoodDataSource,
      selectItemsProvider: this.warehouseGoodSelectItemsProvider,
      query,
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      attributes: data?.attributes,
      characteristics: data?.characteristics,
      cosmeticBrandId: data?.cosmeticBrandId,
      featuredFile: data?.featuredFile,
      files: data?.files,
      name: data?.name,
      price: data?.price,
      slug: data?.slug,
      status: data?.status,
      vendorUrl: data?.vendorUrl,
      warehouseProductId: data?.warehouseProductId,
    });
  }

  private configureWarehouseGoodAvailability(): void {
    const control = this.form.controls.warehouseProductId;
    const canBeWarehouseGoodEdited = this.canBeWarehouseGoodEdited();

    if (isNil(canBeWarehouseGoodEdited)) {
      control.enable();

      return;
    }

    canBeWarehouseGoodEdited ? control.enable() : control.disable();
  }

  private cosmeticStatusSelectItems(): Observable<
    SelectItem<AdminCosmeticStatus>[]
  > {
    return this.cosmeticStatusProvider.selectItems();
  }

  private configureBrandRequirements(required: boolean): void {
    const control = this.form.controls.cosmeticBrandId;

    this.changeRequirementsForControl({ control, required });
  }

  private configureFeaturedFileRequirements(required: boolean): void {
    const control = this.form.controls.featuredFile;

    this.changeRequirementsForControl({ control, required });
  }

  private configureFilesRequirements(required: boolean): void {
    const control = this.form.controls.files;

    this.changeRequirementsForControl({ control, required });
  }

  private configurePriceRequirements(required: boolean): void {
    const control = this.form.controls.price;

    this.changeRequirementsForControl({ control, required });
  }

  private changeRequirementsForControl<Value>(options: {
    required: boolean;
    control: FormControl<Value>;
  }): void {
    const { control, required } = options;

    required
      ? control.setValidators([Validators.required])
      : control.removeValidators([Validators.required]);

    control.updateValueAndValidity();
  }
}
